import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  HeldActionsContext,
  type Announcement,
  type HeldActionsApi,
} from './heldActions';

interface AnnouncementEntry extends Announcement {
  id: string;
}

let sequence = 0;
const nextId = () => `held-${Date.now()}-${(sequence += 1)}`;

export function HeldActionsProvider({ children }: { children: ReactNode }) {
  const [announcements, setAnnouncements] = useState<AnnouncementEntry[]>([]);

  const dismissAnnouncement = useCallback((id: string) => {
    setAnnouncements((entries) => entries.filter((entry) => entry.id !== id));
  }, []);

  const announce = useCallback((announcement: Announcement) => {
    const id = nextId();
    const duration = announcement.durationMs ?? 6_000;
    setAnnouncements((entries) => [...entries.slice(-2), { ...announcement, id }]);
    setTimeout(() => dismissAnnouncement(id), duration);
  }, [dismissAnnouncement]);

  const api = useMemo<HeldActionsApi>(() => ({
    announce,
  }), [announce]);

  return (
    <HeldActionsContext.Provider value={api}>
      {children}
      <div className="ua-slab-viewport" aria-live="polite" aria-relevant="additions">
        {announcements.map((entry) => (
          <SlabToast
            key={entry.id}
            message={entry.message}
            actionLabel={entry.actionLabel}
            onAction={entry.onAction ? () => { entry.onAction?.(); dismissAnnouncement(entry.id); } : undefined}
            onDismiss={() => dismissAnnouncement(entry.id)}
          />
        ))}
      </div>
    </HeldActionsContext.Provider>
  );
}

function SlabToast({
  message,
  actionLabel,
  onAction,
  onDismiss,
}: {
  message: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <div className="ua-slab" role="status">
      <span className="ua-slab-message">{message}</span>
      {actionLabel && onAction && (
        <button type="button" className="ua-slab-action" onClick={onAction}>{actionLabel}</button>
      )}
      {onDismiss && (
        <button type="button" className="ua-slab-dismiss" aria-label="Dismiss" onClick={onDismiss}>×</button>
      )}
    </div>
  );
}
